import { useEffect, useState } from "react";
import Hub from "../../../../api/Hub";
import Div from "../../../../components/base/Div";
import Indicator from "../../../../components/base/Indicator";
import { toastSuccess } from "../../../../components/base/Toast";
import { DrawerForm } from "../../../../components/drawers/DrawerForm";
import TextBox from "../../../../components/form/TextBox";
import FormTitle from "../../../../components/form/FormTitle";
import { API_ROUTES } from "../../../../core/Constants";
import PBXServer from "../models/PBXServer";


interface IProps {
    // Undefined means create; otherwise the server being edited.
    serverId?: string
    onClose?: () => void
    onSave?: () => void
}

export default function PBXServerForm(props: IProps) {

    const hub = new Hub<PBXServer>(API_ROUTES.PBXServers);

    const [loading, loadingSet] = useState<boolean>(!!props.serverId);
    const [model, modelSet] = useState<PBXServer>();

    const isUpdate = !!props.serverId;

    useEffect(() => {
        if (!props.serverId) return;
        loadingSet(true);
        hub.Find(props.serverId, res => {
            if (res.success) modelSet(res.data);
            loadingSet(false);
        });
    }, [props.serverId]);

    return (
        <DrawerForm id="drawer-pbx-server"
            title={isUpdate ? "Edit Server" : "Create New Server"}
            titleIcon="server"
            size="large"
            buttonText={isUpdate ? "Save Server" : "Create Server"}
            focusOn={loading ? undefined : "name"}
            hideButtons={loading}
            onClose={props.onClose}
            onSubmitted={props.onSave}
            onSubmit={(body, callback) => {

                if (isUpdate) {
                    hub.Update(props.serverId ?? "", body, res => {
                        if (res.success) toastSuccess("Server has been updated successfully!");
                        callback!(res);
                    });
                }
                else {
                    hub.Create(body, res => {
                        if (res.success) toastSuccess("Server has been created successfully!");
                        callback!(res);
                    });
                }

            }}>

            <Div visible={loading} className="p-20 text-center">
                <Indicator text="Please wait..." />
            </Div>

            {
                // The fields are uncontrolled (defaultValue), so they must not mount before
                // the server is fetched -- otherwise they would stay empty on edit.
                !loading &&
                <>
                    <FormTitle title="Server" />
                    <TextBox name="name" label="Server Name" defaultValue={model?.name} autoFocus />
                    <TextBox name="host" label="Host" defaultValue={model?.host} />
                    <TextBox name="hostLocal" label="Host (Local)" defaultValue={model?.hostLocal} />

                    <FormTitle title="Web Socket (WebRTC)" />
                    <TextBox label="WSS Host" name="wssHost" defaultValue={model?.wssHost} />
                    <TextBox label="WSS Port" name="wssPort" placeholder="8089" defaultValue={model?.wssPort?.toString()} numeric maxLength={4} />
                    <TextBox label="WSS Path" name="wssPath" placeholder="/ws" defaultValue={model?.wssPath} />
                    <TextBox label="Default Extension" name="defaultWebRTCExtension" numeric maxLength={6} defaultValue={model?.defaultWebRTCExtension} />
                    <TextBox label="Secret" name="defaultWebRTCSecret" defaultValue={model?.defaultWebRTCSecret} />

                    <FormTitle title="FreePBX API" />
                    <TextBox label="Url" name="pbxApiUrl" defaultValue={model?.pbxApiUrl} />
                    <TextBox label="Client Id" name="pbxClientId" defaultValue={model?.pbxClientId} />
                    <TextBox label="Client Secret" name="pbxClientSecret" defaultValue={model?.pbxClientSecret} />

                    <FormTitle title="Asterisk API" />
                    <TextBox label="Url" name="asteriskApiUrl" defaultValue={model?.asteriskApiUrl} />
                    {/* Stored as asteriskClientSecret -- the API authenticates with this single
                        key in the X-Api-Key header, so there is no client id to go with it. */}
                    <TextBox label="API Key" name="asteriskClientSecret" defaultValue={model?.asteriskClientSecret} />

                    <FormTitle title="Asterisk Context" />
                    <TextBox label="Database" name="asteriskContextDatabase" defaultValue={model?.asteriskContextDatabase} />
                    <TextBox label="User" name="asteriskContextUser" defaultValue={model?.asteriskContextUser} />
                    <TextBox label="Password" name="asteriskContextPassword" defaultValue={model?.asteriskContextPassword} />

                    <FormTitle title="CDR Context" />
                    <TextBox label="Database" name="cdrContextDatabase" defaultValue={model?.cdrContextDatabase} />
                    <TextBox label="User" name="cdrContextUser" defaultValue={model?.cdrContextUser} />
                    <TextBox label="Password" name="cdrContextPassword" defaultValue={model?.cdrContextPassword} />

                    <FormTitle title="Asterisk AMI" />
                    <TextBox label="Host" name="asteriskAMIHost" defaultValue={model?.asteriskAMIHost} />
                    <TextBox label="Username" name="asteriskAMIUsername" defaultValue={model?.asteriskAMIUsername} />
                    <TextBox label="Password" name="asteriskAMIPassword" defaultValue={model?.asteriskAMIPassword} />

                    <FormTitle title="Digital Ocean Firewall" />
                    <TextBox label="Authorization Token" name="digitalOceanAuthToken" defaultValue={model?.digitalOceanAuthToken} />
                    <TextBox label="Rule ID" name="digitalOceanRuleID" defaultValue={model?.digitalOceanRuleID} />
                </>
            }

        </DrawerForm>
    )
}
